/**
 * The reading drawing family — the ids `SensorAnimation.vue` draws.
 *
 * The hub declares `settings.reading_type` on the classes that report readings.
 * `auto` is the default and means "read the unit, then the name" — the rule a
 * plain sensor has always used, so a `°C` sensor is still a thermometer without
 * anyone picking one.
 */
import { sensorKindOf } from '../../devices/deviceWidgetTypes';
import { variantFromSetting, variantValues, type VariantFamily } from './types';

export const READING_VARIANTS: VariantFamily = {
  drawing: 'reading',
  settingKey: 'reading_type',
  title: ['Reading type', 'Vrsta meritve'],
  description: [
    'What this number means. The tile draws it; the reading itself is unchanged.',
    'Kaj ta številka pomeni. Ploščica to nariše; meritev ostane enaka.',
  ],
  options: [
    { value: 'auto', icon: 'settings_suggest', label: ['Automatic — from the unit', 'Samodejno — po enoti'] },
    { value: 'battery', icon: 'battery_full', label: ['Battery', 'Baterija'] },
    { value: 'temperature', icon: 'device_thermostat', label: ['Temperature', 'Temperatura'] },
    { value: 'humidity', icon: 'water_drop', label: ['Humidity', 'Vlaga'] },
    { value: 'pressure', icon: 'compress', label: ['Pressure', 'Tlak'] },
    { value: 'co2', icon: 'co2', label: ['CO₂', 'CO₂'] },
    { value: 'air', icon: 'air', label: ['Air quality', 'Kakovost zraka'] },
    { value: 'illuminance', icon: 'light_mode', label: ['Illuminance', 'Osvetljenost'] },
    { value: 'power', icon: 'bolt', label: ['Power', 'Moč'] },
    { value: 'energy', icon: 'electric_meter', label: ['Energy', 'Energija'] },
    { value: 'voltage', icon: 'electric_bolt', label: ['Voltage', 'Napetost'] },
    { value: 'current', icon: 'cable', label: ['Current', 'Tok'] },
    { value: 'wind', icon: 'wind_power', label: ['Wind speed', 'Hitrost vetra'] },
    { value: 'windDirection', icon: 'explore', label: ['Wind direction', 'Smer vetra'] },
    { value: 'rain', icon: 'rainy', label: ['Rain', 'Dež'] },
    { value: 'waterLevel', icon: 'waves', label: ['Water level', 'Nivo vode'] },
    { value: 'signal', icon: 'signal_cellular_alt', label: ['Signal', 'Signal'] },
    { value: 'noise', icon: 'volume_up', label: ['Noise', 'Hrup'] },
    { value: 'distance', icon: 'straighten', label: ['Distance', 'Razdalja'] },
    { value: 'speed', icon: 'speed', label: ['Speed', 'Hitrost'] },
    { value: 'flow', icon: 'water', label: ['Flow', 'Pretok'] },
    { value: 'generic', icon: 'sensors', label: ['None — keep the glyph', 'Brez — obdrži ikono'] },
  ],
  defaultValue: 'auto',
  resolve: (device) =>
    variantFromSetting(device.settings, 'reading_type', variantValues(READING_VARIANTS)) ?? sensorKindOf(device),
};
